(function () {
  const isOutOfStock = (variant) => {
    if (!variant) return true;
    // `available` is Shopify's computed source of truth — it accounts for
    // inventory management, policy, and quantity. Untracked variants are
    // always available regardless of quantity, so we don't check qty alone.
    return variant.available === false;
  };

  const buildWaitlistTags = (productHandle, variant) => {
    const tags = ['back-in-stock'];

    if (productHandle) {
      tags.push(String(productHandle).trim());
    }

    return tags.join(',');
  };

  const updateWaitlistForm = (
    form,
    {
      productHandle = '',
      productTitle = '',
      productUrl = '',
      productImage = '',
      variant = null,
    } = {}
  ) => {
    if (!form) return;

    const normalizedHandle = String(productHandle || '').trim();
    const normalizedTitle = String(productTitle || '').trim();
    const normalizedUrl = String(productUrl || '').trim();
    const normalizedImage = String(productImage || '').trim();
    const normalizedSku = String(variant?.sku || '').trim();

    const normalizedVariantId = String(variant?.id || '').replace(/\D/g, '');
    form.dataset.waitlistProductHandle = normalizedHandle;
    form.dataset.waitlistProductTitle = normalizedTitle;
    form.dataset.waitlistProductUrl = normalizedUrl;
    form.dataset.waitlistProductImage = normalizedImage;
    form.dataset.waitlistSku = normalizedSku;
    form.dataset.waitlistVariantId = normalizedVariantId;
    form.dataset.waitlistTags = buildWaitlistTags(normalizedHandle, variant);
  };

  const toggleProductStockState = (productId, variant, scope = document) => {
    if (!productId || !scope?.querySelectorAll) return;

    const outOfStock = isOutOfStock(variant);
    scope.querySelectorAll(`[data-product-stock-state][data-product-id="${productId}"]`).forEach((block) => {
      const shouldShowOutOfStock = block.dataset.stockState === 'out';
      block.classList.toggle('hidden', shouldShowOutOfStock ? !outOfStock : outOfStock);
    });
  };

  const WAITLIST_FEEDBACK = {
    success: '[data-waitlist-success]',
    confirm: '[data-waitlist-confirm]',
    unsubscribed: '[data-waitlist-unsubscribed]',
    archived: '[data-waitlist-archived]',
    undeliverable: '[data-waitlist-undeliverable]',
    unreachable: '[data-waitlist-unreachable]',
    error: '[data-waitlist-error]',
  };

  const toggleWaitlistFeedback = (form, state = '') => {
    const root = form.closest('[data-waitlist-root]') || form.parentElement;
    Object.entries(WAITLIST_FEEDBACK).forEach(([key, selector]) => {
      const el = form.querySelector(selector) || root?.querySelector(selector);
      if (el) el.classList.toggle('hidden', state !== key);
    });
  };

  // The server now reports whether the alert can actually be delivered. Showing
  // "you're on the list" regardless is what left 510 customers waiting on
  // notifications that could never arrive.
  const feedbackForDeliverability = (payload) => {
    const state = payload?.deliverability;

    if (state === 'reachable') return 'success';
    if (state === 'needs_confirmation') return 'confirm';
    // 'unreachable' covers three different situations and they need different words.
    // Telling an archived shopper "this address is unsubscribed" is false - they never
    // opted out - and it hides the one remedy that works, their own re-subscribe.
    if (state === 'unreachable') {
      const status = payload?.deliverabilityStatus;
      if (status === 'cleaned') return 'undeliverable';
      if (status === 'archived') return 'archived';
      if (status === 'unsubscribed') return 'unsubscribed';
      return 'unreachable';
    }

    // 'unknown' means our own lookup failed, not that the contact is bad. The request
    // is saved either way, so don't alarm the shopper over our problem.
    return 'success';
  };

  const setWaitlistPending = (form, isPending) => {
    const submitButton = form.querySelector('[data-waitlist-submit]');
    if (!submitButton) return;
    submitButton.disabled = isPending;
    submitButton.classList.toggle('opacity-70', isPending);
    submitButton.classList.toggle('cursor-not-allowed', isPending);
  };

  const submitWaitlistForm = async (form) => {
    const emailInput = form.querySelector('[data-waitlist-email]');
    const email = String(emailInput?.value || '').trim();
    const consentInput = form.querySelector('[data-waitlist-consent]');
    const consentError = form.querySelector('[data-waitlist-consent-error]');

    if (!email) {
      toggleWaitlistFeedback(form, 'error');
      return;
    }

    // Affirmative consent is the basis for subscribing them in Mailchimp. Without it
    // we have no business emailing them, so don't send the request at all.
    if (consentInput && !consentInput.checked) {
      toggleWaitlistFeedback(form, '');
      if (consentError) consentError.classList.remove('hidden');
      return;
    }

    if (consentError) consentError.classList.add('hidden');
    toggleWaitlistFeedback(form, '');
    setWaitlistPending(form, true);

    try {
      const response = await fetch('/apps/wishlist/back-in-stock', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({
          email,
          sku: form.dataset.waitlistSku || '',
          variantId: form.dataset.waitlistVariantId || '',
          consent: true,
        }),
      });

      if (!response.ok) {
        throw new Error(`Waitlist request failed with status ${response.status}`);
      }

      const payload = await response.json().catch(() => null);
      toggleWaitlistFeedback(form, feedbackForDeliverability(payload));
      form.reset();
    } catch (error) {
      toggleWaitlistFeedback(form, 'error');
    } finally {
      setWaitlistPending(form, false);
    }
  };

  const bindWaitlistForms = () => {
    if (document.documentElement.dataset.waitlistBound === 'true') return;
    document.documentElement.dataset.waitlistBound = 'true';

    document.addEventListener('click', async (event) => {
      const button = event.target.closest('[data-waitlist-submit]');
      if (!button) return;

      const form = button.closest('[data-waitlist-form]');
      if (!form) return;

      event.preventDefault();
      await submitWaitlistForm(form);
    });

    document.addEventListener('submit', async (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !form.matches('[data-waitlist-form]')) return;

      event.preventDefault();
      await submitWaitlistForm(form);
    });
  };

  const refreshInventory = async () => {
    const match = window.location.pathname.match(/\/products\/([^/?#]+)/);
    if (!match) return;
    const handle = match[1];

    const stockEl = document.querySelector('[data-product-stock-state][data-product-id]');
    const productId = stockEl?.dataset.productId;
    if (!productId) return;

    try {
      const response = await fetch(`/products/${handle}.js?_=${Date.now()}`, { cache: 'no-store' });
      if (!response.ok) return;
      const product = await response.json();

      const selectedVariantId = document.querySelector('[data-variant-input]')?.value;
      const variant = selectedVariantId
        ? product.variants.find((v) => String(v.id) === String(selectedVariantId))
        : product.variants[0];

      if (variant) toggleProductStockState(productId, variant);
    } catch (_) {}
  };

  window.BlueRoseStockState = {
    bindWaitlistForms,
    buildWaitlistTags,
    isOutOfStock,
    refreshInventory,
    toggleProductStockState,
    updateWaitlistForm,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      bindWaitlistForms();
      refreshInventory();
    }, { once: true });
  } else {
    bindWaitlistForms();
    refreshInventory();
  }

  window.addEventListener('pageshow', (event) => {
    if (event.persisted) refreshInventory();
  });
})();
